<script setup>
  import { ref, inject, onMounted, computed, watchEffect, nextTick } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useRegionStore } from '@/stores/regionStore'
  import { useBranchStore } from '@/stores/branchStore'
  import { useStaffStore } from '@/stores/staffStore'
  import { useCompanyStore } from '@/stores/companyStore'
  import { industries } from '@/components/logics/Consts'
  import { convertToHalfWidthNumber, convertToFullWidth } from '@/components/logics/Util'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const regionStore = useRegionStore()
  const branchStore = useBranchStore()
  const staffStore = useStaffStore()
  const companyStore = useCompanyStore()

  const breadcrumbs = [
    { name: 'ダッシュボード', url: '/dashboard' },
    { name: '会社一覧', url: '/company' },
    { name: '事業場一覧', path: null },
  ]

  // data ///////////////////////////////////////////
  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')

  // 編集用データ
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  // computed ///////////////////////////////////////

  // 削除ボタンの有効無効
  const isShowDeleteButton = computed(() => {
    return isEdit.value
  })

  // 現在選択可能な監督署のリスト
  const currentOfficeOptions = computed(() => {
    return regionStore.officeOptions(branchStore.formData.laborBureauCode)
  })

  // 会社名
  const svgText = ref(null)
  const svgTextWidth = ref(0)
  const companyName = computed(() => {
    nextTick(() => {
      if (svgText.value) {
        svgTextWidth.value = svgText.value.getComputedTextLength()
      }
    })
    return companyStore.currentCompanyName
  })

  // 入力ハンドラ /////////////////////////////////////////

  // 全角文字入力の処理
  const handleFullWidthInput = (event, key) => {
    branchStore.formData[key] = convertToFullWidth(event.target.value)
  }

  // 電話番号入力時の処理
  const handlePhoneInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    branchStore.formData.phone[index] = halfWidthValue
  }

  // FAX番号入力時の処理
  const handleFaxInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    branchStore.formData.fax[index] = halfWidthValue
  }

  // 郵便番号入力時の処理
  const handlePostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const value = event.target.value
    const halfWidthValue = convertToHalfWidthNumber(value)
    branchStore.formData.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (branchStore.isPostalCodeFilled()) {
      branchStore.setAddress(await regionStore.getAddressWithPostalCode(branchStore.postalCodeWithHyphen))
      // 労働局と監督署を設定
      const newBureau = regionStore.getPrefectureCodeByName(branchStore.formData.prefecture)
      if (branchStore.formData.laborBureauCode !== newBureau) {
        branchStore.formData.laborBureauCode = newBureau
        branchStore.formData.laborOfficeCode = ''
      }
    }
  }

  // 労働保険番号入力時の処理
  const handleLaborNumberInput = (event, field) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    branchStore.formData[field] = halfWidthValue
  }

  // 労働局が変更されたときのハンドラー
  function handleLaborBureauChange() {
    // 労働局が変更されたら監督署をリセット
    branchStore.formData.laborOfficeCode = ''
  }

  // 都道府県が変更されたときのハンドラー
  function handlePrefectureChange(event) {
    // 都道府県が変更されたら監督署をリセット
    branchStore.formData.laborBureauCode = event.target.value
    branchStore.formData.laborOfficeCode = ''
  }

  // メールアドレス入力時：使えない文字を即座に除去
  const handleEmailInput = event => {
    // メールで使える文字以外を除去（a-zA-Z0-9._%+-@のみ許可）
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')

    // フィルタリング後の値をv-modelに反映
    branchStore.formData.email = filtered

    // input欄にも即時反映
    event.target.value = filtered
  }

  // UI ////////////////////////////////////////

  // methods ////////////////////////////////////////
  onMounted(async () => {
    branchStore.branches = []
    // テーブル定義の初期化
    branchStore.initTableList()

    // データ取得
    await regionStore.getPrefectures()
    await regionStore.getLaborData()

    // 会社IDを取得して事業場一覧を取得
    const companyId = route.params.companyId
    if (companyId) {
      if (!(await branchStore.getBranches(companyId))) {
        // 事業場が取得できなかった場合、会社一覧に戻る
        router.push({ name: 'company' })
        return
      }
      // 選択中の会社情報がない、または異なる会社IDの場合
      if (!companyStore.selectedCompany || companyStore.selectedCompany.id !== companyId) {
        // APIから会社情報を取得
        await companyStore.getCompanyNameById(companyId)
      }
    }

    // クエリのactionがnewの場合、新規登録ダイアログを開く
    if (route.query.action === 'new') {
      openEditDialog()
    }
  })

  // 事業場編集ダイアログを開く
  const openEditDialog = async item => {
    isEdit.value = !!item
    await branchStore.setFormData(item?.id)

    showPopupEdit.value = true
  }

  // 事業場保存
  const onSave = async () => {
    const message = await branchStore.saveBranch()
    if (message) {
      showPopupEdit.value = false
      await dialogStore.showMessageDialog('確認', message)
    }
  }

  // 事業場削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', '本当に削除しますか？')) {
      const message = await branchStore.deleteBranch()
      if (message) {
        showPopupEdit.value = false
        await dialogStore.showMessageDialog('確認', message)
      }
    }
  }

  // 事業場複製
  const onCopy = async () => {
    branchStore.copyBranch()
    isEdit.value = false
  }

  // 行のアクション
  const handleRowAction = ({ table, item }) => {
    // 従業員一覧へ遷移
    if (table.key === 'staff') {
      branchStore.selectedBranch = item.id
      router.push({
        name: 'staff',
        params: {
          companyId: route.params.companyId,
        },
        query: {
          branchId: item.id,
        },
      })
    }
  }

  // 戻るボタン
  const goBack = () => {
    companyStore.currentCompanyName = ''
    router.push({ name: 'company' })
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="goBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>
        <svg :width="svgTextWidth" height="31" xmlns="http://www.w3.org/2000/svg" :viewBox="`0 0 ${svgTextWidth} 31`">
          <text ref="svgText" x="0" y="24" font-size="27" fill="var(--active-color)">{{ companyName }} 事業場一覧</text>
        </svg>
      </h2>

      <button type="button" class="new" @click="openEditDialog()">事業場追加</button>

      <div class="filters">
        <!-- 検索ボックス -->
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>

        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>

    <div class="main-body">
      <DataTable :table-list="branchStore.tableList" :data-list="branchStore.branches" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditDialog" @length="length = $event" @row-action="handleRowAction"></DataTable>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- 編集ダイアログ -->
  <EditDialog v-model:visible="showPopupEdit" :show-save-button="true" :show-delete-button="isShowDeleteButton" :show-copy-button="isEdit" :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>
      {{ isEdit ? '編集' : '新規登録' }}
    </template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>事業場名称</dt>
          <dd>
            <input id="branch-name" type="text" v-model="branchStore.formData.name" v-autokana:branch-name @input="handleFullWidthInput($event, 'name')" placeholder="全角で入力してください" />
          </dd>
          <dt>事業場名称カナ</dt>
          <dd>
            <input id="branch-name-kana" type="text" v-model="branchStore.formData.nameKana" @input="handleFullWidthInput($event, 'nameKana')" placeholder="全角カタカナで入力してください" />
          </dd>
          <dt>郵便番号</dt>
          <dd>
            <div class="postal-code-grid">
              <input type="text" v-model="branchStore.formData.postalCode[0]" placeholder="123" maxlength="3" @input="handlePostalCodeInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="branchStore.formData.postalCode[1]" placeholder="4567" maxlength="4" @input="handlePostalCodeInput($event, 1)" />
            </div>
          </dd>
          <dt>都道府県</dt>
          <dd>
            <div class="select">
              <select v-model="branchStore.formData.prefecture" @change="handlePrefectureChange">
                <option value="">選択してください</option>
                <option v-for="item in regionStore.prefectureNames" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
          </dd>
          <dt>住所</dt>
          <dd>
            <input id="address" type="text" v-model="branchStore.formData.address" v-autokana:address @input="handleFullWidthInput($event, 'address')" placeholder="全角で入力してください" />
          </dd>
          <dt>住所フリガナ</dt>
          <dd>
            <input id="address-kana" type="text" v-model="branchStore.formData.addressKana" @input="handleFullWidthInput($event, 'addressKana')" placeholder="全角カタカナで入力してください" />
          </dd>
          <dt>電話番号</dt>
          <dd>
            <div class="phone-grid">
              <input type="text" v-model="branchStore.formData.phone[0]" placeholder="012" maxlength="5" @input="handlePhoneInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="branchStore.formData.phone[1]" placeholder="3456" maxlength="4" @input="handlePhoneInput($event, 1)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="branchStore.formData.phone[2]" placeholder="7890" maxlength="4" @input="handlePhoneInput($event, 2)" />
            </div>
          </dd>
          <dt>FAX番号</dt>
          <dd>
            <div class="phone-grid">
              <input type="text" v-model="branchStore.formData.fax[0]" placeholder="012" maxlength="5" @input="handleFaxInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="branchStore.formData.fax[1]" placeholder="3456" maxlength="4" @input="handleFaxInput($event, 1)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="branchStore.formData.fax[2]" placeholder="7890" maxlength="4" @input="handleFaxInput($event, 2)" />
            </div>
          </dd>
          <dt>業種</dt>
          <dd>
            <input type="text" v-model="branchStore.formData.industry" @input="handleFullWidthInput($event, 'industry')" placeholder="全角で入力してください" />
            <div class="select mini">
              <select @change="branchStore.formData.industry = $event.target.value">
                <option v-for="industry in industries" :key="industry" :value="industry">{{ industry }}</option>
              </select>
            </div>
          </dd>
          <dt>代表者名</dt>
          <dd>
            <div class="name-grid">
              <input id="representative-last" type="text" v-model="branchStore.formData.representativeLastName" v-autokana:representative-last-kana @input="handleFullWidthInput($event, 'representativeLastName')" placeholder="姓" />
              <input id="representative-first" type="text" v-model="branchStore.formData.representativeFirstName" v-autokana:representative-first-kana @input="handleFullWidthInput($event, 'representativeFirstName')" placeholder="名" />
            </div>
          </dd>
          <dt>代表者名フリガナ</dt>
          <dd>
            <div class="name-grid">
              <input id="representative-last-kana" type="text" v-model="branchStore.formData.representativeLastKana" @input="handleFullWidthInput($event, 'representativeLastKana')" placeholder="セイ" />
              <input id="representative-first-kana" type="text" v-model="branchStore.formData.representativeFirstKana" @input="handleFullWidthInput($event, 'representativeFirstKana')" placeholder="メイ" />
            </div>
          </dd>
          <dt>代表者職名</dt>
          <dd>
            <input type="text" v-model="branchStore.formData.representativeTitle" @input="handleFullWidthInput($event, 'representativeTitle')" placeholder="全角で入力してください" />
          </dd>
          <dt>代表者部門名</dt>
          <dd>
            <input id="representative-division" type="text" v-model="branchStore.formData.representativeDivision" v-autokana:representative-division-kana @input="handleFullWidthInput($event, 'representativeDivision')" placeholder="全角で入力してください" />
          </dd>
          <dt>代表者部門名フリガナ</dt>
          <dd>
            <input id="representative-division-kana" type="text" v-model="branchStore.formData.representativeDivisionKana" @input="handleFullWidthInput($event, 'representativeDivisionKana')" placeholder="全角カタカナで入力してください" />
          </dd>
          <dt>メールアドレス</dt>
          <dd>
            <input type="email" v-model="branchStore.formData.email" placeholder="メールアドレスを半角英数字で入力してください" @input="handleEmailInput" />
          </dd>
          <dt>管轄労働局</dt>
          <dd>
            <div class="select">
              <select v-model="branchStore.formData.laborBureauCode" @change="handleLaborBureauChange">
                <option value="">選択してください</option>
                <option v-for="bureau in regionStore.bureauOptions" :key="bureau.code" :value="bureau.code">
                  {{ bureau.name }}
                </option>
              </select>
            </div>
          </dd>

          <dt>労働基準監督署</dt>
          <dd>
            <div class="select">
              <select v-model="branchStore.formData.laborOfficeCode" :disabled="!branchStore.formData.laborBureauCode">
                <option value="">選択してください</option>
                <option v-for="office in currentOfficeOptions" :key="office.code" :value="office.code">
                  {{ office.name }}
                </option>
              </select>
            </div>
          </dd>
          <dt>労働保険 基幹番号</dt>
          <dd>
            <input type="text" v-model="branchStore.formData.laborBasicNumber" @input="handleLaborNumberInput($event, 'laborBasicNumber')" placeholder="半角数字で入力してください" maxlength="6" />
          </dd>
          <dt>労働保険 枝番号</dt>
          <dd>
            <input type="text" v-model="branchStore.formData.laborBranchNumber" @input="handleLaborNumberInput($event, 'laborBranchNumber')" placeholder="半角数字で入力してください" maxlength="3" />
          </dd>
          <dt>労働保険 被一括事業場番号</dt>
          <dd>
            <input type="text" v-model="branchStore.formData.laborConsolidatedNumber" @input="handleLaborNumberInput($event, 'laborConsolidatedNumber')" placeholder="半角数字で入力してください" maxlength="4" />
          </dd>

          <dt>労災保険加入区分</dt>
          <dd class="checkbox-group">
            <label>
              <input type="checkbox" v-model="branchStore.formData.hasLaborInsuranceRegular" />
              常用労働者
            </label>
            <label>
              <input type="checkbox" v-model="branchStore.formData.hasLaborInsuranceExecutive" />
              役員（労働者扱い）
            </label>
            <label>
              <input type="checkbox" v-model="branchStore.formData.hasLaborInsuranceTemporary" />
              臨時労働者
            </label>
          </dd>

          <dt>メモ</dt>
          <dd>
            <textarea v-model="branchStore.formData.memo"></textarea>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .checkbox-group {
    gap: 8px;
  }

  .company-subtitle {
    font-size: 0.9rem;
    color: #666;
  }

  .material-symbols-outlined {
    vertical-align: middle;
  }
</style>
